// API Configuration for different environments
import { 
  getEnvironmentConfig, 
  isProductionEnv, 
  isStaging, 
  shouldShowDebug,
  currentEnvironment 
} from './environment';

// Check if running in a native mobile app (Capacitor)
export const isNativeMobile = (): boolean => {
  return typeof window !== 'undefined' && 
    (window as any).Capacitor && 
    (window as any).Capacitor.isNativePlatform();
};

// Check if we're in local development (localhost or 127.0.0.1)
export const isLocalDevelopment = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Check if we're in production (virtoly.com domain)
export const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'virtoly.com' || hostname === 'www.virtoly.com';
};

const getApiBaseUrl = (): string => {
  // For mobile apps, use the environment config (set at build time)
  if (isNativeMobile()) {
    const config = getEnvironmentConfig();
    
    // Log environment info for debugging (only in non-production)
    if (shouldShowDebug()) {
      console.log(`🌍 Environment: ${config.displayName}`);
      console.log(`📡 API URL: ${config.apiUrl}`);
    }
    
    // Allow localStorage override for local development testing
    // This is useful when testing mobile app against a local server
    const customApiUrl = localStorage.getItem('VIRTOLY_API_URL');
    if (customApiUrl) {
      console.warn('⚠️ Using custom API URL from localStorage:', customApiUrl);
      return customApiUrl;
    }
    
    return config.apiUrl;
  }
  
  // For web development (localhost), use relative URLs (Vite proxy handles API calls)
  // For production web (virtoly.com), also use relative URLs
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

// Helper function to build full API URLs
export const buildApiUrl = (path: string): string => {
  const baseUrl = API_BASE_URL;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  if (baseUrl) {
    return `${baseUrl}${cleanPath}`;
  }
  
  return cleanPath;
};

/**
 * Base URL for redirects (e.g. Stripe return_url/refresh_url).
 * On native app we use the API base URL so Stripe redirects to the website (https).
 * On web we use the current origin.
 */
export const getRedirectBaseUrl = (): string => {
  if (isNativeMobile()) {
    const config = getEnvironmentConfig();
    const customApiUrl = localStorage.getItem('VIRTOLY_API_URL');
    const base = customApiUrl || config.apiUrl;
    // Strip trailing slash and any path so we get origin only (e.g. https://virtoly.com)
    try {
      const u = new URL(base);
      return u.origin;
    } catch {
      return base.replace(/\/+$/, '').replace(/\/[^/]*$/, '') || base;
    }
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

// WebSocket/Reverb configuration
export const getWebSocketHost = (): string => {
  // For mobile apps, use the environment config (set at build time)
  if (isNativeMobile()) {
    const config = getEnvironmentConfig();
    
    // Allow override via localStorage for mobile development testing
    const customWsHost = localStorage.getItem('VIRTOLY_WS_HOST');
    if (customWsHost) {
      console.warn('⚠️ Using custom WebSocket host from localStorage:', customWsHost);
      return customWsHost;
    }
    
    return config.wsHost;
  }
  
  // First check environment variable (highest priority)
  if (import.meta.env.VITE_REVERB_HOST) {
    return import.meta.env.VITE_REVERB_HOST;
  }
  
  // For web, use the current hostname (works for both local dev and production)
  return typeof window !== 'undefined' ? window.location.hostname : 'localhost';
};

export const getWebSocketConfig = () => {
  const isMobile = isNativeMobile();
  const isLocal = isLocalDevelopment();
  
  // For mobile apps, use the environment config
  if (isMobile) {
    const envConfig = getEnvironmentConfig();
    
    // Allow localStorage overrides for testing
    const customWsPort = localStorage.getItem('VIRTOLY_WS_PORT');
    const customWsScheme = localStorage.getItem('VIRTOLY_WS_SCHEME');
    
    return {
      host: getWebSocketHost(),
      port: customWsPort ? Number(customWsPort) : envConfig.wsPort,
      scheme: customWsScheme || envConfig.wsScheme,
      key: import.meta.env.VITE_REVERB_APP_KEY || 'virtoly-reverb-key-12345',
    };
  }
  
  // Determine defaults based on web environment
  let defaultPort: number;
  let defaultScheme: string;
  
  if (isLocal) {
    // Local development defaults
    defaultPort = Number(import.meta.env.VITE_REVERB_PORT || 8080);
    defaultScheme = import.meta.env.VITE_REVERB_SCHEME || 'http';
  } else {
    // Production web
    defaultPort = Number(import.meta.env.VITE_REVERB_PORT || 443);
    defaultScheme = import.meta.env.VITE_REVERB_SCHEME || 'https';
  }
  
  return {
    host: getWebSocketHost(),
    port: defaultPort,
    scheme: defaultScheme,
    key: import.meta.env.VITE_REVERB_APP_KEY || 'virtoly-reverb-key-12345',
  };
};

// Export environment info for debugging
export { currentEnvironment, isProductionEnv, isStaging };
